// ============================================
// ASSISTANT IA D'UN DOSSIER — APPELS AU SERVEUR
// ============================================
// Routes (backend, controllers/ai.controller.ts) :
//   GET  /admin/ai/etat                       { actif, cle, suggestions, extraction } (booléens)
//   POST /admin/ai/summary/:ticketId          synthèse (IA en cache, sinon « locale »)
//   POST /admin/ai/brouillon/:ticketId        brouillon proposé (rien n'est envoyé)
//        corps { ton?, consigne?, destination?, brouillonActuel? }
//        409 « spam possible », 503 IA ou suggestions coupées
//   POST /admin/ai/pieces-jointes/:ticketId   lecture des photos et PDF (rien n'est écrit)
//        409 « spam possible », 503 lecture coupée

import { fetchWithAuth } from '../../services/api';

export interface EtatAssistantIA {
  actif: boolean;
  cle: boolean;
  /** Synthèse IA et brouillon disponibles. */
  suggestions: boolean;
  /** Bouton « Lire les pièces jointes » disponible. */
  extraction: boolean;
}

export type EtatClient = 'satisfait' | 'neutre' | 'preoccupe' | 'frustre' | 'urgent';

export interface SyntheseDossier {
  ticketNumber: number;
  source: 'ia' | 'locale';
  raisonLocale: string | null;
  resume: string;
  pointsCles: string[];
  etatClient: EtatClient;
  prochainesEtapes: string[];
  progression: number;
  genereeLe: string;
  enCache: boolean;
}

export type DestinationBrouillon = 'client' | 'note_interne';
export type TonBrouillon = 'standard' | 'plus_court' | 'plus_formel' | 'plus_technique';
export type ChampEquipementDossier = 'equipmentBrand' | 'equipmentModel' | 'serialNumber' | 'errorCode';

export interface ActionProposee {
  type: 'statut' | 'priorite' | 'type' | 'equipement' | 'reponse_type';
  champ?: ChampEquipementDossier;
  valeur: string;
  raison: string;
  reponseType?: { id: string; titre: string; contenu: string };
}

export interface BrouillonConseiller {
  brouillon: string;
  destination: DestinationBrouillon;
  resumeSituation: string;
  informationsManquantes: string[];
  actionsProposees: ActionProposee[];
  confiance: 'faible' | 'moyenne' | 'elevee';
  brouillonEcarte: string | null;
  fournisseur: string;
  modele: string;
}

export interface OptionsBrouillon {
  ton?: TonBrouillon;
  consigne?: string;
  destination?: DestinationBrouillon;
  brouillonActuel?: string;
}

export interface ValeurLue {
  champ: 'marque' | 'modele' | 'numero_serie' | 'code_erreur' | 'date_achat';
  libelle: string;
  valeur: string;
  confiance: 'faible' | 'moyenne' | 'elevee';
  piece: { id: string; nom: string } | null;
  champDossier: ChampEquipementDossier | null;
  valeurActuelle: string | null;
}

export interface LecturePieces {
  lues: Array<{ id: string; nom: string; mimeType: string }>;
  ignorees: Array<{ id: string; nom: string; raison: string }>;
  valeurs: ValeurLue[];
  remarque: string;
}

const id = (ticketId: string) => encodeURIComponent(ticketId);

export const iaApi = {
  etat: () => fetchWithAuth<EtatAssistantIA>('/admin/ai/etat'),
  synthese: (ticketId: string) => fetchWithAuth<SyntheseDossier>(`/admin/ai/summary/${id(ticketId)}`, { method: 'POST' }),
  brouillon: (ticketId: string, options: OptionsBrouillon = {}) =>
    fetchWithAuth<BrouillonConseiller>(`/admin/ai/brouillon/${id(ticketId)}`, { method: 'POST', body: JSON.stringify(options) }),
  lirePiecesJointes: (ticketId: string) =>
    fetchWithAuth<LecturePieces>(`/admin/ai/pieces-jointes/${id(ticketId)}`, { method: 'POST' }),
};
